export interface SolarCellPublicConfig {
  API_URL: string;
  STRIPE_PUBLIC_KEY: string;
  GOOGLE_CLIENT_ID: string;
  DEPLOYMENT_MODE: 'demo_local' | 'private_staging' | 'private_production';
}

type WindowWithSolarCellConfig = Window & {
  __SOLARCELL_CONFIG__?: Partial<SolarCellPublicConfig>;
};

const REQUIRED_KEYS: (keyof SolarCellPublicConfig)[] = [
  'API_URL',
  'STRIPE_PUBLIC_KEY',
  'GOOGLE_CLIENT_ID',
];

const PLACEHOLDER_TOKENS = [
  'placeholder',
  'change_me',
  'changeme',
  'replace_me',
  'required_',
  'example',
  'dummy',
  'fake',
  'todo',
];

// DF42: reject values that look like they leaked a server-side secret into
// public runtime config (this file is served unauthenticated as /config.js).
const SECRET_TOKENS = ['sk_live_', 'sk_test_', 'whsec_', 'client_secret', 'password='];

function clean(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function valueLooksUnsafe(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return true;
  return PLACEHOLDER_TOKENS.some((token) => normalized.includes(token));
}

function getRuntimeConfig(): Partial<SolarCellPublicConfig> {
  if (typeof window === 'undefined') return {};
  return (window as WindowWithSolarCellConfig).__SOLARCELL_CONFIG__ ?? {};
}

function getViteFallbackConfig(): Partial<SolarCellPublicConfig> {
  return {
    API_URL: import.meta.env.VITE_API_URL,
    STRIPE_PUBLIC_KEY: import.meta.env.VITE_STRIPE_PUBLIC_KEY,
    GOOGLE_CLIENT_ID: import.meta.env.VITE_GOOGLE_CLIENT_ID,
    DEPLOYMENT_MODE: import.meta.env.VITE_DEPLOYMENT_MODE as SolarCellPublicConfig['DEPLOYMENT_MODE'] | undefined,
  };
}

/**
 * Validates a resolved public config, throwing on the first problem found.
 *
 * Extracted from `getPublicConfig()` (DF42 V4.2) so it can be unit-tested
 * directly instead of only through the `import.meta.env`-dependent getter.
 * `strict` gates the "required keys must not look like a placeholder" check,
 * which only applies to production builds.
 */
export function validatePublicConfig(config: SolarCellPublicConfig, strict = true): SolarCellPublicConfig {
  if (strict) {
    const missing = REQUIRED_KEYS.filter((key) => valueLooksUnsafe(String(config[key])));
    if (missing.length > 0) {
      throw new Error(
        `[SolarCells] Missing or unsafe public runtime configuration: ${missing.join(', ')}. ` +
        'The frontend container must generate /config.js from runtime environment variables before Nginx starts.',
      );
    }
  }

  if (!['demo_local', 'private_staging', 'private_production'].includes(config.DEPLOYMENT_MODE)) {
    throw new Error('[SolarCells] DEPLOYMENT_MODE must be demo_local, private_staging or private_production.');
  }

  if (!/^(\/|https?:\/\/)/.test(config.API_URL)) {
    throw new Error('[SolarCells] API_URL must be HTTP(S) or root-relative.');
  }

  if (config.STRIPE_PUBLIC_KEY && !config.STRIPE_PUBLIC_KEY.startsWith('pk_')) {
    throw new Error('[SolarCells] STRIPE_PUBLIC_KEY must be a public Stripe key starting with pk_.');
  }

  for (const [key, raw] of Object.entries(config)) {
    const value = String(raw).toLowerCase();
    if (SECRET_TOKENS.some((token) => value.includes(token))) {
      throw new Error(`[SolarCells] ${key} contains a server-side secret.`);
    }
  }

  return config;
}

export function getPublicConfig(): SolarCellPublicConfig {
  const runtimeConfig = getRuntimeConfig();
  const viteFallback = getViteFallbackConfig();

  const config: SolarCellPublicConfig = {
    API_URL: clean(runtimeConfig.API_URL) || clean(viteFallback.API_URL) || (import.meta.env.DEV ? 'http://localhost:3001/api/v1' : ''),
    STRIPE_PUBLIC_KEY: clean(runtimeConfig.STRIPE_PUBLIC_KEY) || clean(viteFallback.STRIPE_PUBLIC_KEY),
    GOOGLE_CLIENT_ID: clean(runtimeConfig.GOOGLE_CLIENT_ID) || clean(viteFallback.GOOGLE_CLIENT_ID),
    DEPLOYMENT_MODE: (clean(runtimeConfig.DEPLOYMENT_MODE) || clean(viteFallback.DEPLOYMENT_MODE) || 'demo_local') as SolarCellPublicConfig['DEPLOYMENT_MODE'],
  };

  return validatePublicConfig(config, import.meta.env.PROD);
}

export const publicConfig = getPublicConfig();
